import React, { FC, ReactElement } from 'react';

import globalStyles from 'assets/styles/global.module.scss';
import { useUserData } from 'context';
import { ROLE_NAME } from 'enums';
import { PATHS } from 'types';
import {
  Button,
  Card,
  Headline2,
  Headline4,
  Hint,
  Lock,
  MainContainer,
  StatusBadge,
  Text,
  Util,
} from 'views/components';
import { formatDate } from 'views/pages/ManageOrganization/SubscriptionManagement/formatDate';

const FrozenOrganization: FC = (): ReactElement => {
  const { user, billingStatus, frozenOrganization } = useUserData();

  const isAdmin = user?.role.name === ROLE_NAME.ADMIN;
  const freezeReason = billingStatus?.freezeReason;
  const gracePeriodEndsAt =
    frozenOrganization?.gracePeriodEndsAt || billingStatus?.gracePeriodEndsAt || null;
  const reasonText =
    freezeReason === 'STARTER_SEAT_OVERAGE'
      ? 'This organization has more than five active members on the Starter plan.'
      : 'A payment for this organization needs attention.';

  return (
    <MainContainer>
      <div className={globalStyles['no-data-card']}>
        <Card cardPadding="2.5rem" maxWidth="480px" textAlign="center">
          <Util marginBottom="16px">
            <Lock />
          </Util>
          <Util marginBottom="16px">
            <Headline2>Organization access is frozen</Headline2>
          </Util>
          <Util marginBottom="16px">
            <Text align="center">
              {reasonText}{' '}
              {frozenOrganization?.message ||
                'Resolve the organization billing issue to restore access.'}
            </Text>
          </Util>

          {gracePeriodEndsAt && (
            <Util marginBottom="24px">
              <StatusBadge variant="neutral">
                Grace period ended {formatDate(gracePeriodEndsAt)}
              </StatusBadge>
            </Util>
          )}

          {isAdmin ? (
            <>
              <Hint mode="warning">
                <Util marginBottom="8px">
                  <Headline4>How to restore access</Headline4>
                </Util>
                <Text>
                  Update your billing details or reduce active members to the Starter
                  limit. Access returns automatically once the issue is resolved.
                </Text>
              </Hint>
              <Util marginTop="24px" marginBottom="12px">
                <Button link={PATHS.MANAGE_ORGANIZATION_SUBSCRIPTION_MANAGEMENT} modBlock>
                  Manage billing
                </Button>
              </Util>
              <Util marginBottom="12px">
                <Button
                  link={PATHS.MANAGE_ORGANIZATION_USER_MANAGEMENT}
                  mode="secondary"
                  modBlock
                >
                  Manage members
                </Button>
              </Util>
            </>
          ) : (
            <Text align="center" variant="bodySmall" color="var(--sk-text-subtle)">
              Please contact your organization administrator to resolve the billing issue.
            </Text>
          )}
        </Card>
      </div>
    </MainContainer>
  );
};

export { FrozenOrganization };
